import React, { useState } from "react";
import { motion } from "framer-motion";
import { 
    MapPin, 
    Clock, 
    Star, 
    Phone, 
    Calendar, 
    Video, 
    CheckCircle, 
    Baby, 
    Activity, 
    Stethoscope, 
    TrendingUp,
    Navigation,
    Users,
    Award,
    Heart,
    Shield,
    Search
} from "lucide-react";

const EndocrinologistNearMe: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalOpen2, setIsModalOpen2] = useState(false);

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);
    const openModal2 = () => setIsModalOpen2(true);
    const closeModal2 = () => setIsModalOpen2(false);

    const nearbyLocations = [
        { 
            name: "AIIMS Delhi", 
            distance: "2.5 km", 
            area: "Ansari Nagar",
            days: "Monday & Thursday", 
            time: "9am-12pm",
            metro: "AIIMS Metro Station",
            specialties: ["Growth Disorders", "Pediatric Diabetes"]
        },
        { 
            name: "Safdarjung Hospital", 
            distance: "3.2 km", 
            area: "Safdarjung Enclave",
            days: "Tuesday & Friday", 
            time: "2-5pm",
            metro: "Green Park Metro",
            specialties: ["Thyroid Issues", "Puberty Disorders"]
        },
        { 
            name: "Max Hospital Saket", 
            distance: "4.1 km", 
            area: "Saket",
            days: "Wednesday & Saturday", 
            time: "10am-1pm",
            metro: "Malviya Nagar Metro",
            specialties: ["Childhood Obesity", "PCOS in Teens"]
        },
        { 
            name: "Apollo Hospital", 
            distance: "5.8 km", 
            area: "Mathura Road",
            days: "Monday & Wednesday", 
            time: "4-7pm",
            metro: "Tughlakabad Metro",
            specialties: ["Growth Hormone", "Insulin Therapy"]
        },
        { 
            name: "Fortis Shalimar Bagh", 
            distance: "12.5 km", 
            area: "Shalimar Bagh",
            days: "Thursday & Friday", 
            time: "11am-2pm",
            metro: "Shalimar Bagh Metro",
            specialties: ["Type 1 Diabetes", "Adrenal Disorders"]
        },
        { 
            name: "BLK Hospital", 
            distance: "6.3 km", 
            area: "Pusa Road",
            days: "Tuesday & Saturday", 
            time: "3-6pm",
            metro: "Rajendra Place Metro",
            specialties: ["Bone Disorders", "CGM Setup"]
        }
    ];

    const delhiAreas = [
        { area: "Central Delhi", localities: ["Connaught Place", "Karol Bagh", "Paharganj", "Daryaganj"] },
        { area: "South Delhi", localities: ["Saket", "Hauz Khas", "Lajpat Nagar", "Greater Kailash"] },
        { area: "North Delhi", localities: ["Civil Lines", "Model Town", "Kamla Nagar", "Chandni Chowk"] },
        { area: "West Delhi", localities: ["Rajouri Garden", "Janakpuri", "Dwarka", "Punjabi Bagh"] },
        { area: "East Delhi", localities: ["Laxmi Nagar", "Preet Vihar", "Mayur Vihar", "Patparganj"] },
        { area: "New Delhi", localities: ["India Gate", "Lodhi Road", "Khan Market", "Vasant Vihar"] }
    ];

    const searchTips = [
        {
            tip: "Check Metro Connectivity",
            description: "Choose locations near metro stations for easier commute with children"
        },
        {
            tip: "Hospital Reputation",
            description: "Look for specialists associated with reputed hospitals like AIIMS, Safdarjung"
        },
        {
            tip: "Child-Friendly Environment",
            description: "Ensure the clinic has pediatric-friendly facilities and staff"
        },
        {
            tip: "Emergency Support",
            description: "Verify 24/7 emergency contact availability for urgent situations"
        }
    ];

    const conditions = [
        { name: "Short Stature in Children", urgency: "Moderate", icon: Baby },
        { name: "Pediatric Type 1 Diabetes", urgency: "High", icon: Activity },
        { name: "Thyroid Disorders in Kids", urgency: "Moderate", icon: Stethoscope },
        { name: "Early/Delayed Puberty", urgency: "Moderate", icon: TrendingUp },
        { name: "Childhood Obesity", urgency: "Low", icon: Heart },
        { name: "Growth Hormone Deficiency", urgency: "High", icon: Baby }
    ];

    return (
        <div className="bg-white">
            {/* SEO-Rich Header Section */}
            <section className="bg-gradient-to-br from-purple-50 to-pink-50 py-16 px-4">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-12"
                    >
                        <h1 className="text-4xl md:text-6xl font-bold text-purple-900 mb-6">
                            Best Pediatric Endocrinologist Near Me in Delhi
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-700 mb-4">
                            Find Top-Rated Child Hormone Specialists in Your Area - Dr. Swati Kanodia
                        </p>
                        <p className="text-lg text-purple-600 mb-8">
                            ✓ 6 Convenient Delhi Locations ✓ Metro Connected ✓ AIIMS Standards ✓ 5000+ Happy Families
                        </p>
                        
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={openModal}
                                className="bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-purple-700 transition-all flex items-center justify-center"
                            >
                                <Navigation className="mr-2 w-5 h-5" />
                                Find Nearest Clinic
                            </button>
                            <button
                                onClick={openModal2}
                                className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-700 transition-all flex items-center justify-center"
                            >
                                <Video className="mr-2 w-5 h-5" />
                                Online Consultation
                            </button>
                        </div>
                    </motion.div>

                    {/* Quick Location Search */}
                    <div className="bg-white p-6 rounded-lg shadow-lg max-w-4xl mx-auto">
                        <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
                            <Search className="inline w-6 h-6 mr-2" />
                            Find Pediatric Endocrinologist Near Your Location
                        </h2>
                        <div className="grid md:grid-cols-3 gap-4">
                            <div className="text-center p-4 bg-purple-50 rounded-lg">
                                <MapPin className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                                <h3 className="font-semibold">Within 5km</h3>
                                <p className="text-sm text-gray-600">4 Locations Available</p>
                            </div>
                            <div className="text-center p-4 bg-blue-50 rounded-lg">
                                <Clock className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                                <h3 className="font-semibold">Same Day</h3>
                                <p className="text-sm text-gray-600">Appointments Available</p>
                            </div>
                            <div className="text-center p-4 bg-green-50 rounded-lg">
                                <Star className="w-8 h-8 text-green-600 mx-auto mb-2" />
                                <h3 className="font-semibold">4.9/5 Rating</h3>
                                <p className="text-sm text-gray-600">Based on 500+ Reviews</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Detailed Location Information */}
            <section className="py-20 bg-white">
                <div className="max-w-6xl mx-auto px-4">
                    <h2 className="text-3xl md:text-5xl font-bold text-purple-900 text-center mb-12">
                        Pediatric Endocrinologist Locations Near You in Delhi
                    </h2>

                    <div className="grid lg:grid-cols-2 gap-8">
                        {nearbyLocations.map((location, index) => (
                            <motion.div
                                key={index}
                                className="bg-gradient-to-br from-white to-purple-50 p-6 rounded-lg shadow-lg border hover:shadow-xl transition-shadow"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-800 mb-2">{location.name}</h3>
                                        <p className="text-purple-600 font-semibold mb-1">{location.area}</p>
                                        <div className="flex items-center text-green-600 mb-2">
                                            <Navigation className="w-4 h-4 mr-1" />
                                            <span className="text-sm font-medium">{location.distance} from city center</span>
                                        </div>
                                    </div>
                                    <div className="bg-green-100 px-3 py-1 rounded-full">
                                        <span className="text-green-800 text-sm font-semibold">Available</span>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <div className="flex items-center">
                                        <Clock className="w-5 h-5 text-purple-600 mr-3" />
                                        <div>
                                            <p className="font-semibold text-gray-800">{location.days}</p>
                                            <p className="text-purple-600">{location.time}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center">
                                        <MapPin className="w-5 h-5 text-blue-600 mr-3" />
                                        <p className="text-gray-700">Nearest Metro: <span className="font-semibold">{location.metro}</span></p>
                                    </div>

                                    <div className="pt-2">
                                        <p className="text-sm font-semibold text-gray-800 mb-2">Specialties Available:</p>
                                        <div className="flex flex-wrap gap-2">
                                            {location.specialties.map((specialty, idx) => (
                                                <span key={idx} className="bg-pink-100 text-pink-800 px-2 py-1 rounded-full text-xs">
                                                    {specialty}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <button
                                        onClick={openModal}
                                        className="w-full bg-purple-600 text-white py-2 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                                    >
                                        Book Appointment Here
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Area Coverage Section */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-6xl mx-auto px-4">
                    <h2 className="text-3xl md:text-5xl font-bold text-purple-900 text-center mb-12">
                        Areas We Serve - Pediatric Endocrinology Care Across Delhi
                    </h2>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {delhiAreas.map((region, index) => (
                            <motion.div
                                key={index}
                                className="bg-white p-6 rounded-lg shadow-lg"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <h3 className="text-xl font-bold text-purple-900 mb-4">{region.area}</h3>
                                <div className="space-y-2">
                                    {region.localities.map((locality, idx) => (
                                        <div key={idx} className="flex items-center">
                                            <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                            <span className="text-gray-700">{locality}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <p className="text-sm text-purple-600 font-medium">
                                        <MapPin className="w-4 h-4 inline mr-1" />
                                        Nearest clinic within 10km radius
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <div className="mt-12 bg-gradient-to-r from-purple-100 to-pink-100 p-8 rounded-lg text-center">
                        <h3 className="text-2xl font-bold text-purple-900 mb-4">
                            Can't Find Your Area? We're Still Here to Help!
                        </h3>
                        <p className="text-gray-700 mb-6">
                            Dr. Swati Kanodia provides pediatric endocrinology consultation to families across entire Delhi NCR region
                        </p>
                        <button
                            onClick={openModal2}
                            className="bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                        >
                            Book Online Consultation
                        </button>
                    </div>
                </div>
            </section>

            {/* Common Conditions Section */}
            <section className="py-20 bg-white">
                <div className="max-w-6xl mx-auto px-4">
                    <h2 className="text-3xl md:text-5xl font-bold text-purple-900 text-center mb-12">
                        Common Pediatric Endocrine Conditions We Treat Near You
                    </h2>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {conditions.map((condition, index) => {
                            const IconComponent = condition.icon;
                            return (
                                <motion.div
                                    key={index}
                                    className="bg-gradient-to-br from-white to-purple-50 p-6 rounded-lg shadow-lg border hover:shadow-xl transition-all"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <div className="flex items-start mb-4">
                                        <IconComponent className="w-8 h-8 text-purple-600 mr-3 flex-shrink-0" />
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-800 mb-2">{condition.name}</h3>
                                            <div className="flex items-center">
                                                <span className="text-sm text-gray-600 mr-2">Priority:</span>
                                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                                    condition.urgency === 'High' ? 'bg-red-100 text-red-800' :
                                                    condition.urgency === 'Moderate' ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-green-100 text-green-800'
                                                }`}>
                                                    {condition.urgency}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-gray-600 text-sm mb-4">
                                        Expert pediatric endocrinology care available at all Delhi locations
                                    </p>
                                    <button
                                        onClick={openModal}
                                        className="w-full bg-purple-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors"
                                    >
                                        Find Nearest Specialist
                                    </button>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Tips for Finding Right Doctor */}
            <section className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
                <div className="max-w-6xl mx-auto px-4">
                    <h2 className="text-3xl md:text-5xl font-bold text-purple-900 text-center mb-12">
                        How to Choose the Right Pediatric Endocrinologist Near You
                    </h2>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-6">Essential Tips for Parents</h3>
                            <div className="space-y-4">
                                {searchTips.map((tip, index) => (
                                    <div key={index} className="bg-white p-4 rounded-lg shadow-md">
                                        <h4 className="font-semibold text-purple-900 mb-2">{tip.tip}</h4>
                                        <p className="text-gray-600 text-sm">{tip.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white p-8 rounded-lg shadow-lg">
                            <h3 className="text-2xl font-bold text-purple-900 mb-6">Why Choose Dr. Swati Kanodia?</h3>
                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <Award className="w-6 h-6 text-yellow-500 mr-3 mt-1 flex-shrink-0" />
                                    <div>
                                        <h4 className="font-semibold text-gray-800">AIIMS Association</h4>
                                        <p className="text-gray-600 text-sm">Connected with India's premier medical institution</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <Users className="w-6 h-6 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                                    <div>
                                        <h4 className="font-semibold text-gray-800">5000+ Children Treated</h4>
                                        <p className="text-gray-600 text-sm">Extensive experience across all pediatric endocrine conditions</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <MapPin className="w-6 h-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                                    <div>
                                        <h4 className="font-semibold text-gray-800">6 Delhi Locations</h4>
                                        <p className="text-gray-600 text-sm">Convenient access from anywhere in Delhi NCR</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <Shield className="w-6 h-6 text-purple-500 mr-3 mt-1 flex-shrink-0" />
                                    <div>
                                        <h4 className="font-semibold text-gray-800">Child-Friendly Environment</h4>
                                        <p className="text-gray-600 text-sm">Specially designed clinics for comfortable pediatric care</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-20 bg-white">
                <div className="max-w-4xl mx-auto px-4">
                    <h2 className="text-3xl md:text-5xl font-bold text-purple-900 text-center mb-12">
                        Frequently Asked Questions
                    </h2>

                    <div className="space-y-6">
                        <div className="bg-purple-50 p-6 rounded-lg">
                            <h3 className="text-lg font-bold text-purple-900 mb-3">
                                How do I find the nearest pediatric endocrinologist in Delhi?
                            </h3>
                            <p className="text-gray-700">
                                Dr. Swati Kanodia has 6 convenient locations across Delhi including AIIMS, Safdarjung, Saket, and more. 
                                Use our location finder above to identify the clinic nearest to your area with metro connectivity details.
                            </p>
                        </div>

                        <div className="bg-blue-50 p-6 rounded-lg">
                            <h3 className="text-lg font-bold text-blue-900 mb-3">
                                What conditions require a pediatric endocrinologist?
                            </h3>
                            <p className="text-gray-700">
                                Short stature, growth delays, diabetes, thyroid disorders, early/delayed puberty, obesity, 
                                PCOS in teens, and hormonal imbalances in children all require specialized pediatric endocrine care.
                            </p>
                        </div>

                        <div className="bg-green-50 p-6 rounded-lg">
                            <h3 className="text-lg font-bold text-green-900 mb-3">
                                How quickly can I get an appointment?
                            </h3>
                            <p className="text-gray-700">
                                Same-day and next-day appointments are often available across our Delhi locations. 
                                Emergency consultations can be arranged within hours for urgent pediatric endocrine conditions.
                            </p>
                        </div>

                        <div className="bg-pink-50 p-6 rounded-lg">
                            <h3 className="text-lg font-bold text-pink-900 mb-3">
                                Do you accept insurance for pediatric endocrinology treatments?
                            </h3>
                            <p className="text-gray-700">
                                Yes, most major health insurance plans are accepted at our associated hospitals including AIIMS, 
                                Max, Apollo, and Fortis. We also offer affordable self-pay options for all families.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-20 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold mb-6">
                        Find the Best Pediatric Endocrinologist Near You Today
                    </h2>
                    <p className="text-xl mb-8 text-purple-100">
                        Don't let distance be a barrier to your child's health. Multiple Delhi locations available.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={openModal}
                            className="bg-white text-purple-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors flex items-center justify-center"
                        >
                            <Navigation className="mr-2 w-5 h-5" />
                            Find Nearest Location
                        </button>
                        <button
                            onClick={openModal2}
                            className="bg-green-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-600 transition-colors flex items-center justify-center"
                        >
                            <Video className="mr-2 w-5 h-5" />
                            Book Online Now
                        </button>
                    </div>
                </div>
            </section>

            {/* Appointment Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
                        <button 
                            onClick={closeModal}
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                        >
                            ✕
                        </button>

                        <div className="text-center">
                            <MapPin className="w-16 h-16 text-purple-600 mx-auto mb-4" />
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">Find Nearest Clinic</h3>
                            <p className="text-gray-600 mb-6">
                                Book appointment at the most convenient Delhi location for your family
                            </p>

                            <div className="space-y-4">
                                <div className="bg-purple-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-purple-900 mb-2">6 Delhi Locations</h4>
                                    <p className="text-gray-700">AIIMS, Safdarjung, Saket, Mathura Road, Shalimar Bagh, Pusa Road</p>
                                </div>

                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-900 mb-2">Metro Connected</h4>
                                    <p className="text-gray-700">All locations easily accessible via Delhi Metro</p>
                                </div>

                                <div className="bg-green-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-green-900 mb-2">Same Day Appointments</h4>
                                    <p className="text-gray-700">Quick booking available for urgent consultations</p>
                                </div>
                            </div>

                            <div className="mt-6">
                                <a
                                    href="https://storynavigation.com/user/drswatikanodia"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-purple-600 text-white px-6 py-3 rounded-lg inline-block font-semibold hover:bg-purple-700 transition-colors"
                                >
                                    Book Nearest Location
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Online Consultation Modal */}
            {isModalOpen2 && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
                        <button 
                            onClick={closeModal2}
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                        >
                            ✕
                        </button>

                        <div className="text-center">
                            <Video className="w-16 h-16 text-green-600 mx-auto mb-4" />
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">Online Consultation</h3>
                            <p className="text-gray-600 mb-6">
                                Expert pediatric endocrinology care from anywhere in Delhi NCR
                            </p>

                            <div className="space-y-4">
                                <div className="bg-green-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-green-900 mb-2">Video Consultation</h4>
                                    <p className="text-gray-700">Face-to-face consultation via secure video call</p>
                                </div>

                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-900 mb-2">Available 6 Days a Week</h4>
                                    <p className="text-gray-700">Monday to Saturday: 11am - 7pm</p>
                                    <p className="text-sm text-gray-600">Flexible slots for working parents</p>
                                </div>

                                <div className="bg-purple-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-purple-900 mb-2">What You Need</h4>
                                    <p className="text-gray-700">Stable internet connection & previous reports</p>
                                </div>
                            </div>

                            <div className="mt-6">
                                <a
                                    href="https://storynavigation.com/user/drswatikanodia"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-green-600 text-white px-6 py-3 rounded-lg inline-block font-semibold hover:bg-green-700 transition-colors"
                                >
                                    Start Online Consultation
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            </div>
        
    );
};

export default EndocrinologistNearMe;